/**
 * useScheduleAutoRefresh.ts
 * Silent background polling for the Machine Schedule (UX overhaul — M8).
 *
 * Every ~45s it quietly re-fetches bookings + conflicts (no spinner, grid stays
 * mounted) so another user's edits appear without a manual reload. Polling
 * pauses while the tab is hidden or `paused` is true (a drag is active, the
 * detail drawer is open, or a mutation is in flight) so it never clobbers an
 * in-progress edit. Also refreshes immediately when the tab regains focus.
 *
 * Returns a "last synced" relative label for the footer.
 */

import { useEffect, useRef, useState } from 'react';

interface UseScheduleAutoRefreshArgs {
  reload: (opts?: { quiet?: boolean }) => Promise<void>;
  /** When true, skip the next tick (drag active / drawer open / mutation pending). */
  paused: boolean;
  intervalMs?: number;
}

function relativeLabel(since: number | null, now: number): string {
  if (since === null) return '—';
  const secs = Math.max(0, Math.round((now - since) / 1000));
  if (secs < 10) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.round(mins / 60)}h ago`;
}

export function useScheduleAutoRefresh({
  reload,
  paused,
  intervalMs = 45_000,
}: UseScheduleAutoRefreshArgs): { lastSyncedLabel: string } {
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(() => Date.now());
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  const pausedRef = useRef(paused);
  useEffect(() => { pausedRef.current = paused; }, [paused]);

  // Poll loop.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (pausedRef.current || document.hidden) return;
      void reload({ quiet: true }).then(() => setLastSyncedAt(Date.now()));
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [reload, intervalMs]);

  // Refresh immediately when the tab regains focus.
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden && !pausedRef.current) {
        void reload({ quiet: true }).then(() => setLastSyncedAt(Date.now()));
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [reload]);

  // Keep the relative label fresh.
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, []);

  return { lastSyncedLabel: relativeLabel(lastSyncedAt, nowTick) };
}
